import { Link } from "@tanstack/react-router";
import { PuzzleIcon, SearchIcon, SettingsIcon } from "lucide-react";
import type { ReactNode } from "react";

import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "../../lib/utils";
import { SettingsSection } from "../settings/settingsLayout";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../ui/empty";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { Skeleton } from "../ui/skeleton";
import { PluginRow } from "./PluginRow";
import { PLUGIN_ROW_CLASS, PLUGIN_ROW_CONTROL_CLASS } from "./pluginRowChrome";
import { pluginConnectionCapabilities, type PluginConnectionAttempts } from "./pluginConnections";
import {
  isPluginConnected,
  sortedPlugins,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "./pluginsPage.logic";

const INSTALLED_SKELETON_KEYS: ReadonlyArray<string> = ["installed-1", "installed-2"];
const ROW_SKELETON_KEYS: ReadonlyArray<string> = ["row-1", "row-2", "row-3"];
const EMPTY_PLUGIN_CONNECTION_ATTEMPTS: PluginConnectionAttempts = {};

function availablePlugins(input: {
  readonly plugins: ReadonlyArray<ForgePlugin>;
  readonly query: string;
}): ReadonlyArray<ForgePlugin> {
  const normalizedQuery = input.query.trim().toLowerCase();
  return sortedPlugins(input.plugins).filter((plugin) => {
    if (isPluginConnected(input.plugins, plugin.id)) {
      return false;
    }
    const matchesQuery = normalizedQuery
      ? `${plugin.name} ${plugin.description}`.toLowerCase().includes(normalizedQuery)
      : true;
    return matchesQuery;
  });
}

/**
 * The count that sits opposite a section heading.
 *
 * `tabular-nums` so a list that grows from 9 to 10 does not shift the heading
 * row, and `text-xs` because this is metadata rather than something the user
 * reads to act — the one place the eyebrow scale is correct on this page.
 */
function SectionCount({ count }: { readonly count: number }): ReactNode {
  return count > 0 ? (
    <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
  ) : null;
}

interface PluginViewTabProps {
  readonly label: string;
  readonly to: "/plugins" | "/plugins/skills";
  readonly active: boolean;
}

/**
 * One segment of the Plugins/Skills switcher, in `DESIGN.md` -> "View Switcher".
 *
 * A router `Link`, not an anchor. Both destinations are registered routes now
 * (`routes/_chat.plugins.skills.tsx`), so switching tabs is a client
 * navigation; it used to be a plain `<a href>` because the layout route
 * resolved these pathnames itself and there was nothing to link to, which made
 * every tab change a full document load.
 */
function PluginViewTab({ label, to, active }: PluginViewTabProps): ReactNode {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={cn(
        RESPONSIVE_TEXT_BUTTON_CLASS,
        "inline-flex items-center justify-center rounded-md px-3 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring md:h-7 md:px-2.5 md:text-xs",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </Link>
  );
}

interface PluginsPageChromeProps {
  readonly activeView: "plugins" | "skills";
  readonly refreshButton?: ReactNode;
  readonly search?: ReactNode;
}

/**
 * The page's whole toolbar on one line: view switcher, search, actions.
 *
 * Modelled on t3code's Usage page, where the segmented controls and the refresh
 * button sit together on the right of a single row. Two things followed from
 * that: the search field moved up out of the body, and the page tagline went
 * away entirely. The tagline was restating the crumb — the bar already says
 * "Plugins" — and it cost a full row plus the gap under it, which is what made
 * a three-row catalogue look like an empty page.
 *
 * Below `md` the row wraps: switcher and actions share the first line (the nav
 * takes the free space with `me-auto`), and search takes the second at full
 * width. `flex-wrap` rather than a scroller because a text field is not a pill
 * rail — it needs its width, and wrapping is what gives it one at 320px.
 */
export function PluginsPageChrome({ activeView, refreshButton, search }: PluginsPageChromeProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 px-4 pt-3 sm:px-8">
      <nav
        aria-label="Plugins views"
        className="me-auto flex min-w-0 items-center gap-0.5 rounded-lg bg-muted/40 p-0.5 md:me-0"
      >
        <PluginViewTab label="Plugins" to="/plugins" active={activeView === "plugins"} />
        <PluginViewTab label="Skills" to="/plugins/skills" active={activeView === "skills"} />
      </nav>
      {search ? (
        <div className="order-last w-full min-w-0 md:order-none md:w-56 lg:w-64">{search}</div>
      ) : null}
      <div className="flex items-center gap-1">
        {refreshButton ?? null}
        <Link
          to="/plugins/manage"
          aria-label="Plugin settings"
          className={cn(
            RESPONSIVE_ICON_BUTTON_CLASS,
            "text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <SettingsIcon className="size-4" />
        </Link>
      </div>
    </div>
  );
}

interface PluginsSearchFieldProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
}

/** The catalogue filter, sized by the toolbar cell it is placed in. */
export function PluginsSearchField({ query, onQueryChange }: PluginsSearchFieldProps) {
  return (
    <InputGroup>
      <InputGroupAddon align="inline-start">
        <SearchIcon className="text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupInput
        type="search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search plugins"
        aria-label="Search plugins"
      />
    </InputGroup>
  );
}

interface InstalledPluginsSectionProps {
  readonly plugins: ReadonlyArray<ForgePlugin>;
  readonly loading: boolean;
  readonly attempts?: PluginConnectionAttempts;
  readonly attempt?: PluginConnectionAttempt | null;
  readonly onConnect: (pluginId: ForgePluginId) => void;
  readonly onDisconnect: (pluginId: ForgePluginId) => void;
}

/**
 * Installed plugins, as named rows.
 *
 * This was a strip of bare 40px brand tiles: three anonymous squares with no
 * name, no account, and no way to disconnect, which is why a populated page
 * still read as an empty one. A connected plugin is the thing the user came to
 * check on, so it gets the same row as an available one — and, through
 * `PluginConnectionAction`, the reconnect/disconnect menu that is the way back
 * out of a connection made here.
 */
export function InstalledPluginsSection({
  plugins,
  loading,
  attempts = EMPTY_PLUGIN_CONNECTION_ATTEMPTS,
  attempt = null,
  onConnect,
  onDisconnect,
}: InstalledPluginsSectionProps) {
  const connectedPlugins = sortedPlugins(plugins).filter((plugin) =>
    isPluginConnected(plugins, plugin.id),
  );

  return (
    <SettingsSection
      title="Installed"
      headerAction={<SectionCount count={connectedPlugins.length} />}
    >
      {loading ? (
        <PluginRowsSkeleton keys={INSTALLED_SKELETON_KEYS} />
      ) : connectedPlugins.length > 0 ? (
        connectedPlugins.map((plugin) => (
          <PluginRow
            key={plugin.id}
            plugin={plugin}
            attempt={attempts[plugin.id] ?? attempt}
            onConnect={onConnect}
            onDisconnect={
              (plugin.capabilities ?? pluginConnectionCapabilities(plugin)).disconnect
                ? onDisconnect
                : undefined
            }
          />
        ))
      ) : (
        <p className="px-3 py-3 text-sm text-muted-foreground sm:px-4">
          No plugins connected yet. Connect one below to make it available to every Forge agent.
        </p>
      )}
    </SettingsSection>
  );
}

interface AvailablePluginsSectionProps {
  readonly plugins: ReadonlyArray<ForgePlugin>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly query: string;
  readonly attempts?: PluginConnectionAttempts;
  readonly attempt?: PluginConnectionAttempt | null;
  readonly onConnect: (pluginId: ForgePluginId) => void;
}

/**
 * Plugins the user has not connected yet.
 *
 * One column, not the two-column grid this replaced. A `lg:grid-cols-2` with a
 * 48px gutter is the right answer for a catalog of thirty and the wrong one for
 * a catalog of three: it left a column of empty canvas beside every row and
 * broke the shared right edge the controls line up on.
 */
export function AvailablePluginsSection({
  plugins,
  loading,
  error,
  query,
  attempts = EMPTY_PLUGIN_CONNECTION_ATTEMPTS,
  attempt = null,
  onConnect,
}: AvailablePluginsSectionProps) {
  const visiblePlugins = availablePlugins({ plugins, query });
  const searching = query.trim().length > 0;

  return (
    <SettingsSection
      title="Available"
      headerAction={<SectionCount count={visiblePlugins.length} />}
    >
      {loading ? (
        <AvailablePluginRowsSkeleton />
      ) : error ? (
        <PluginsEmptyState
          title="Couldn't load plugins"
          description={`${error}. Sign in to CMMD, then refresh this page.`}
        />
      ) : visiblePlugins.length > 0 ? (
        visiblePlugins.map((plugin) => (
          <PluginRow
            key={plugin.id}
            plugin={plugin}
            attempt={attempts[plugin.id] ?? attempt}
            onConnect={onConnect}
          />
        ))
      ) : searching ? (
        <PluginsEmptyState
          title="No matches"
          description="No available plugins match your search."
        />
      ) : (
        <PluginsEmptyState
          title="Everything's connected"
          description="All available plugins are installed."
        />
      )}
    </SettingsSection>
  );
}

interface PluginRowsSkeletonProps {
  readonly keys: ReadonlyArray<string>;
}

/**
 * Loading rows shaped like the rows that replace them — same three columns,
 * same row padding — so the list does not resize when the catalog arrives.
 */
function PluginRowsSkeleton({ keys }: PluginRowsSkeletonProps) {
  return (
    <>
      {keys.map((key) => (
        <div key={key} className={PLUGIN_ROW_CLASS}>
          <Skeleton className="size-10 rounded-lg" />
          <div className="min-w-0 space-y-2">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3 w-full max-w-64" />
          </div>
          <Skeleton className={`h-8 w-20 rounded-lg ${PLUGIN_ROW_CONTROL_CLASS}`} />
        </div>
      ))}
    </>
  );
}

export function AvailablePluginRowsSkeleton() {
  return <PluginRowsSkeleton keys={ROW_SKELETON_KEYS} />;
}

interface PluginsEmptyStateProps {
  readonly title: string;
  readonly description: string;
}

/**
 * A centred empty state rather than a left-aligned muted sentence.
 *
 * "All available plugins are installed." is a success, and read as one only
 * once it stopped looking like a row that had failed to render.
 */
function PluginsEmptyState({ title, description }: PluginsEmptyStateProps) {
  return (
    <Empty className="py-10">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <PuzzleIcon />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
